import { useState, useEffect } from "react";
import { Phone, List, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { label: "Home", href: "#hero" },
  { label: "Why Us", href: "#why-us" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? "glass-header shadow-sm border-b border-stone-200/50" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); handleNavClick("#hero"); }}
          data-testid="header-logo"
          className={`font-heading font-extrabold text-lg md:text-xl tracking-tight transition-colors duration-300 ${
            scrolled ? "text-foreground" : "text-white"
          }`}
        >
          Vu's Car Repair
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className={`text-sm font-medium transition-colors duration-300 ${
                scrolled ? "text-muted-foreground hover:text-foreground" : "text-stone-300 hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            asChild
            className="hidden md:inline-flex rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-transform font-heading font-bold text-sm px-6"
            data-testid="header-call-button"
          >
            <a href="tel:5036433040">
              <Phone weight="bold" className="w-4 h-4 mr-1" />
              (503) 643-3040
            </a>
          </Button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 ${
              scrolled ? "text-foreground" : "text-white"
            }`}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav
          className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-stone-200 px-6 pb-6 pt-2"
          data-testid="mobile-nav"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="block py-3 text-base font-medium text-foreground border-b border-stone-200/50 last:border-0"
            >
              {link.label}
            </a>
          ))}
          <Button
            asChild
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground font-heading font-bold"
            data-testid="mobile-call-button"
          >
            <a href="tel:5036433040">
              <Phone weight="bold" className="w-4 h-4 mr-1" />
              Call Now
            </a>
          </Button>
        </nav>
      )}
    </header>
  );
}
